import React from "react";
import { OrderLineDto } from "../../../../api";
import {
  ColDef,
  ICellRendererParams,
  ITooltipParams,
  ValueFormatterParams
} from "ag-grid-community";
import {
  GridIconRenderer,
  GridIconRendererProps
} from "../../../../framework/components/grid/GridIconRenderer";
import moment from "moment";
import { getValidDateFormat } from "../../../../utilities/dateUtils";
import { Grid, createHeaderAndHeaderTooltip } from "../../../../framework/components/grid/Grid";
import { ProductInformationLinkParameters } from "../containers/OrderDetailsView";
import { primitiveSort } from "../../../../utilities/sorting";
import { useTranslation } from "react-i18next";

export interface OrderDetailsGridProps {
  rowData: Array<OrderLineDto> | undefined;
  showProductInformationLink: boolean;
  hasOrderTrackingRights: boolean;
  goToProductInformation: (params: ProductInformationLinkParameters) => void;
}

export const OrderDetailsGrid: React.FunctionComponent<OrderDetailsGridProps> = (props) => {
  const { t } = useTranslation();

  const handleItemSort = (valueA: any, valueB: any): number => {
    if (!isNaN(valueA) && !isNaN(valueB)) {
      return primitiveSort(parseInt(valueA, 10), parseInt(valueB, 10));
    } else if (!isNaN(valueA) || !isNaN(valueB)) {
      return 1;
    } else {
      return primitiveSort(valueA as string, valueB as string);
    }
  };
  const orderLineColumnDefs: ColDef[] = [
    /*When grid is stetched horizontally to fit the screen using sizeColumnsToFit()
    Afrer all columns with maxWidth reach maxWidth point,
    remaining empty space is divided to columns without maxWidth according to their width or minWidth.
    For example, empty space divided to 100px/100px/500px/500px -> 8%/8%/41/41%
    maxWidth is just enough to show whole column header
    */
    {
      field: "item",
      tooltipField: "item",
      ...createHeaderAndHeaderTooltip(t("Item (ABB)")),
      comparator: handleItemSort,
      maxWidth: 125
    },
    {
      field: "itemCustomer",
      tooltipField: "itemCustomer",
      ...createHeaderAndHeaderTooltip(t("Item (Customer)")),
      comparator: handleItemSort,
      maxWidth: 145
    },
    {
      field: "vendorOrderingCode",
      tooltipField: "vendorOrderingCode",
      hide: !props.hasOrderTrackingRights,
      ...createHeaderAndHeaderTooltip(t("Vendor ordering code")),
      minWidth: 100
    },
    {
      field: "customerOrderingCode",
      hide: !props.hasOrderTrackingRights,
      ...createHeaderAndHeaderTooltip(t("Customer ordering code")),
      minWidth: 100
    },
    {
      field: "description",
      hide: !props.hasOrderTrackingRights,
      ...createHeaderAndHeaderTooltip(t("Description")),
      width: 500
    },
    {
      field: "hasProductItems",
      ...createHeaderAndHeaderTooltip(t("Prod. Info."), t("Product information")),
      headerClass: "grid-header-center-with-menu",
      minWidth: 48,
      maxWidth: 145,
      hide: !props.showProductInformationLink,
      cellRenderer: GridIconRenderer,
      cellRendererParams: (params: ICellRendererParams) => {
        const orderLine = params.data as OrderLineDto;
        const cellRendererParams: GridIconRendererProps = {
          ...params,
          showIcon: (orderLine.hasTestCertificates || orderLine.hasSoftwareDocuments) ?? false,
          onClick: () => {
            props.goToProductInformation({
              orderAcknowledgementNumber: orderLine.orderAcknowledgementNumber,
              orderLineItem: orderLine.item
            });
          },
          icon: "abb/double-right"
        };
        return cellRendererParams;
      },
      tooltipValueGetter: (params: ITooltipParams) => {
        if (!params.data) {
          return "";
        }
        const orderLine = params.data as OrderLineDto;
        const availableDocumentTypes: string[] = [];
        if (orderLine?.hasSoftwareDocuments) {
          availableDocumentTypes.push(t("Software available"));
        }
        if (orderLine?.hasTestCertificates) {
          availableDocumentTypes.push(t("Test certificates available"));
        }
        const ret = availableDocumentTypes.join(", ");
        return ret;
      }
    },
    {
      field: "quantity",
      ...createHeaderAndHeaderTooltip(t("Qty")),
      maxWidth: 70
    },
    {
      field: "shippedPlanned",
      ...createHeaderAndHeaderTooltip(t("Planned shipping date")),
      minWidth: 120,
      maxWidth: 190,
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) {
          return "";
        }
        return moment(params.value).format(getValidDateFormat());
      }
    },
    {
      field: "shipped",
      ...createHeaderAndHeaderTooltip(t("Updated shipping date")),
      minWidth: 120,
      maxWidth: 190,
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) {
          return "";
        }
        return moment(params.value).format(getValidDateFormat());
      }
    },
    {
      field: "delayReason",
      ...createHeaderAndHeaderTooltip(t("Reason for delay")),
      maxWidth: 400
    },
    {
      field: "status",
      filter: "agSetColumnFilter",
      menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
      ...createHeaderAndHeaderTooltip(t("Status")),
      valueFormatter: (params: ValueFormatterParams): string => t(params.value),
      minWidth: 100,
      maxWidth: 180
    }
  ];

  return (
    <Grid
      onFirstDataRendered={(params) => {
        params.columnApi.applyColumnState({
          state: [{ colId: "item", sort: "asc" }]
        });
        // Have to call onSortChanged so that it uses the custom comparator for sorting
        params.api.onSortChanged();
        const columns = params.columnApi.getAllColumns();
        if (columns) {
          params.api.sizeColumnsToFit();
        }
      }}
      columnDefs={orderLineColumnDefs}
      rowData={props.rowData ?? []}
      title={t("Order lines")}
      titleHeadingLevel={3}
      domLayout="autoHeight"
      t={t}
    />
  );
};
